'use client';

import { signOut } from 'next-auth/react';
import { api } from '@/shared/lib/api';
import { useSessionStore } from '@/store/sessionStore';

// Margem de segurança: consideramos o token expirado alguns segundos antes do `exp`
// real, para evitar disparar um último pedido que o backend já rejeitaria com 401.
const EXPIRY_SKEW_MS = 5_000;

// Antecedência com que o refresh proativo é disparado, antes do `exp` do token (ver
// useLoginLayout). Suficientemente maior que EXPIRY_SKEW_MS/latência de rede para o
// pedido de refresh ter sempre tempo de completar com o token ainda válido no backend.
export const REFRESH_AHEAD_OF_EXPIRY_MS = 5 * 60 * 1000;

// Descodifica o `exp` (em ms) do payload de um JWT de sessão. NÃO valida a assinatura
// — o backend continua a ser a autoridade final; isto serve apenas para o cliente saber
// quando deixar de confiar no token e forçar o logout proativamente.
export function decodeTokenExpMs(token: string): number | null {
  try {
    const payload = token.split('.')[1];
    if (!payload) return null;
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const json = JSON.parse(atob(base64));
    return typeof json.exp === 'number' ? json.exp * 1000 : null;
  } catch {
    return null;
  }
}

// Devolve o número de ms até à expiração (já com a margem de segurança aplicada),
// ou null se o token for inexistente/malformado/sem `exp`.
export function msUntilExpiry(token: string | null): number | null {
  if (!token) return null;
  const expMs = decodeTokenExpMs(token);
  if (expMs === null) return null;
  return expMs - EXPIRY_SKEW_MS - Date.now();
}

// true se o token estiver ausente, malformado ou (praticamente) expirado.
export function isTokenExpired(token: string | null): boolean {
  if (!token) return true;
  const remaining = msUntilExpiry(token);
  return remaining === null ? true : remaining <= 0;
}

// Guard de reentrância: o signOut provoca um redirect, mas várias fontes (bootstrap,
// timer, 401) podem chamar forceLogout quase em simultâneo. Reposto naturalmente no
// reload que o signOut despoleta.
let loggingOut = false;

// Logout completo por expiração: limpa a sessão de backend (store + localStorage) e
// termina a sessão NextAuth/Azure, devolvendo o utilizador ao ecrã de login.
export function forceLogout(): void {
  if (loggingOut) return;
  loggingOut = true;
  useSessionStore.getState().expireSession();
  signOut({ callbackUrl: '/' });
}

// Liga o interceptor de 401 do api client ao logout completo. Dependência
// unidirecional (sessionExpiry -> api/store), pelo que não há import circular.
api.onUnauthorized = forceLogout;
